// ── Column & group definitions for the Raumbuch spreadsheet ──

export type CellType = 'text' | 'number' | 'status' | 'user' | 'date';
export type SourceType = 'scan' | 'edit';
export type GroupId = 'id' | 'scan' | 'ausbau' | 'technik' | 'prozess';

export interface TableColumn {
  key: string;
  label: string;
  type: CellType;
  width: number;
  group: GroupId;
  source: SourceType;
  readonly?: boolean;
}

export interface GroupMeta {
  label: string;
  color: string;
  bg: string;
  icon?: string; // SVG path or identifier
}

export const GROUP_META: Record<GroupId, GroupMeta> = {
  id:      { label: 'Identifikation',    color: '#6b7280', bg: '#fafafa' },
  scan:    { label: 'Aus Punktwolke',     color: '#0369a1', bg: 'rgba(14,165,233,0.06)', icon: 'scan' },
  ausbau:  { label: 'Ausbau & Material',  color: '#a16207', bg: 'rgba(234,179,8,0.05)',  icon: 'cube' },
  technik: { label: 'Technik & Schutz',   color: '#7c3aed', bg: 'rgba(124,58,237,0.05)', icon: 'bolt' },
  prozess: { label: 'Prozess',            color: '#374151', bg: '#f5f5f5',               icon: 'clock' },
};

export const RAUMBUCH_COLUMNS: TableColumn[] = [
  // — Identifikation
  { key: 'nr',          label: 'Raum-Nr.',        type: 'text',   width: 82,  group: 'id',      source: 'edit' },
  { key: 'name',        label: 'Bezeichnung',     type: 'text',   width: 160, group: 'id',      source: 'edit' },
  { key: 'ebene',       label: 'Ebene',           type: 'text',   width: 70,  group: 'id',      source: 'scan', readonly: true },

  // — Geometrie (aus Punktwolke — readonly)
  { key: 'flaeche',     label: 'Fläche m²',       type: 'number', width: 96,  group: 'scan',    source: 'scan', readonly: true },
  { key: 'hoehe',       label: 'Höhe m',          type: 'number', width: 78,  group: 'scan',    source: 'scan', readonly: true },
  { key: 'volumen',     label: 'Volumen m³',      type: 'number', width: 102, group: 'scan',    source: 'scan', readonly: true },
  { key: 'umfang',      label: 'Umfang m',        type: 'number', width: 90,  group: 'scan',    source: 'scan', readonly: true },
  { key: 'fenster',     label: 'Fenster',         type: 'number', width: 72,  group: 'scan',    source: 'scan', readonly: true },
  { key: 'tueren',      label: 'Türen',           type: 'number', width: 66,  group: 'scan',    source: 'scan', readonly: true },

  // — Ausbau & Material (editierbar)
  { key: 'nutzung',     label: 'Nutzung DIN 277', type: 'text',   width: 140, group: 'ausbau',  source: 'edit' },
  { key: 'boden',       label: 'Bodenbelag',      type: 'text',   width: 140, group: 'ausbau',  source: 'edit' },
  { key: 'wand',        label: 'Wandbelag',       type: 'text',   width: 140, group: 'ausbau',  source: 'edit' },
  { key: 'decke',       label: 'Decke',           type: 'text',   width: 130, group: 'ausbau',  source: 'edit' },
  { key: 'schadstoffe', label: 'Schadstoffe',     type: 'text',   width: 150, group: 'ausbau',  source: 'edit' },

  // — Technik & Schutz
  { key: 'brandschutz', label: 'Brandschutz',     type: 'text',   width: 108, group: 'technik', source: 'edit' },
  { key: 'heizung',     label: 'Heizung',         type: 'text',   width: 130, group: 'technik', source: 'edit' },
  { key: 'lux',         label: 'Lux Ø',           type: 'number', width: 80,  group: 'technik', source: 'edit' },

  // — Prozess
  { key: 'owner',       label: 'Zuständig',       type: 'user',   width: 130, group: 'prozess', source: 'edit' },
  { key: 'faellig',     label: 'Fällig',          type: 'date',   width: 100, group: 'prozess', source: 'edit' },
  { key: 'status',      label: 'Status',          type: 'status', width: 130, group: 'prozess', source: 'edit' },
];

export interface RaumbuchRow {
  id: number;
  volumeId?: number; // Link to ExtrudedPolygonVolume.id
  values: Record<string, string | number | null>;
}

export const STATUS_STYLES: Record<string, { bg: string; fg: string; dot: string }> = {
  'Offen':         { bg: '#fef3c7',                  fg: '#b45309', dot: '#f59e0b' },
  'In Prüfung':    { bg: 'rgba(59,130,246,0.08)',     fg: '#2563eb', dot: '#3b82f6' },
  'Abgeschlossen': { bg: 'rgba(22,163,74,0.08)',      fg: '#15803d', dot: '#16a34a' },
  'Blockiert':     { bg: 'rgba(220,38,38,0.06)',      fg: '#b91c1c', dot: '#dc2626' },
};
